import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangePicker, Spinner } from '@crazyegginc/hatch';
import { fromUnixTime, getUnixTime } from 'date-fns';

import { DateRange } from '/src/components/date-time/DateRange';
import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SitePickerBar } from '/src/components/site-picker';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { usePermissions, useQueryParams, useSite, useTimeZonedFunctions } from '/src/hooks';
import { FEATURES, SPECIAL_DATE_RANGES, TIMEZONES } from '/src/features/_global/constants';

import { DEFAULT_LIMIT, DEFAULT_PRESET_DATE, formatPlatform } from '../constants';
import { adPlatformAnalyticsQuery } from '../queries';
import { SummaryCards } from '../components/SummaryCards';
import { SpendOverTimeChart } from '../components/SpendOverTimeChart';
import { PlatformBreakdown } from '../components/PlatformBreakdown';
import { TopCampaignsTable } from '../components/TopCampaignsTable';
import { NoConnectionsWall } from '../walls/NoConnections';

const DATE_RANGE_PARAM = 'date_range';
const MAX_RANGE_DAYS = 365;
const VALID_SPECIAL_DATE_RANGES = new Set(Object.values(SPECIAL_DATE_RANGES));

export function AdPerformanceDashboard() {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.AD_PLATFORM_ANALYTICS);
  const { sites, selectedSite, selectSite, loadingSites } = useSite();
  const { get: getParam, set: setParam } = useQueryParams();
  const { getConvertedDateRange, startOfDay, endOfDay, subYears } = useTimeZonedFunctions(TIMEZONES.UTC);
  const dateRangeParam = getParam(DATE_RANGE_PARAM);
  const dateSelection = useMemo(() => parseDateRangeParam(dateRangeParam), [dateRangeParam]);
  const normalizedDateRangeParam = useMemo(() => serializeDateRange(dateSelection), [dateSelection]);

  useEffect(() => {
    if (dateRangeParam !== normalizedDateRangeParam) {
      setParam(DATE_RANGE_PARAM, normalizedDateRangeParam);
    }
  }, [dateRangeParam, normalizedDateRangeParam, setParam]);

  const dateRangeConfig = useMemo(
    () => ({
      special: dateSelection.special,
      start_date: dateSelection.startDate,
      end_date: dateSelection.endDate,
    }),
    [dateSelection],
  );

  const convertedDateRange = useMemo(
    () => getConvertedDateRange(dateRangeConfig),
    [dateRangeConfig, getConvertedDateRange],
  );
  const pickerLowerBoundary = startOfDay(subYears(new Date(), 1));
  const pickerUpperBoundary = endOfDay(new Date());

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        setParam(DATE_RANGE_PARAM, value.special);
      } else if (value.start_date && value.end_date) {
        setParam(DATE_RANGE_PARAM, `${getUnixTime(value.start_date)}-${getUnixTime(value.end_date)}`);
      }
    },
    [setParam],
  );

  const queryDateArgs = useMemo(() => {
    if (dateSelection.special) {
      return { presetDate: dateSelection.special };
    }

    return {
      startAt: convertedDateRange.startDate,
      endAt: convertedDateRange.endDate,
    };
  }, [convertedDateRange.endDate, convertedDateRange.startDate, dateSelection.special]);

  const { data, isLoading, isFetching } = useQuery({
    ...adPlatformAnalyticsQuery({
      siteId: selectedSite?.id,
      limit: DEFAULT_LIMIT,
      ...queryDateArgs,
    }),
  });

  const dashboard = data?.adPlatformAnalytics;
  const hasConnections = (dashboard?.connections ?? []).length > 0;
  const loading = loadingSites || (selectedSite && (isLoading || isFetching));

  if (!canViewDashboard) {
    return <DashboardPaywall feature={FEATURES.AD_PLATFORM_ANALYTICS} reason={reason} />;
  }

  return (
    <DashboardPage>
      <SEO title="Ad Performance" />
      <DashHeader title="Ad Performance" />
      <PaymentAlerts />
      <SitePickerBar
        sites={sites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        loading={loadingSites}
        addSiteOption={permissions.can('create', FEATURES.SITES).allowed}
        pageDisplayName="Ad Performance dashboard"
      />
      <div className="flex flex-col gap-6 p-10 !pt-5">
        {loading ? (
          <div className="mt-16 flex w-full items-center justify-center">
            <Spinner />
            <div className="ml-2.5">Loading...</div>
          </div>
        ) : !selectedSite ? null : !hasConnections ? (
          <NoConnectionsWall />
        ) : (
          <>
            <section className="mb-1 flex items-center justify-between gap-4">
              <div className="ml-1 text-lg font-semibold text-black-pearl-500">
                <DateRange dateRange={dateRangeConfig} timeZone={TIMEZONES.UTC} />
              </div>
              <DateRangePicker
                lowerBoundary={pickerLowerBoundary}
                upperBoundary={pickerUpperBoundary}
                startDate={dateSelection.startDate ? fromUnixTime(dateSelection.startDate) : undefined}
                endDate={dateSelection.endDate ? fromUnixTime(dateSelection.endDate) : undefined}
                special={dateSelection.special}
                setDateRange={chooseDateRange}
                setCommonDateRange={chooseDateRange}
                showCommonRanges={true}
                size="lg"
                maxRange={MAX_RANGE_DAYS}
                timezone={TIMEZONES.UTC}
                showTimeZone={true}
              />
            </section>
            <SummaryCards summary={dashboard.summary} />
            <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <SpendOverTimeChart daily={dashboard.daily} />
              <ConnectionPanel connections={dashboard.connections} />
            </section>
            <PlatformBreakdown platformTotals={dashboard.platformTotals} />
            <TopCampaignsTable campaigns={dashboard.topCampaigns} />
          </>
        )}
      </div>
    </DashboardPage>
  );
}

function ConnectionPanel({ connections = [] }) {
  return (
    <section className="rounded-2xl border border-mystic-500 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Connections</p>
      <h2 className="mt-2 text-lg font-semibold text-black-pearl-500">Sync status</h2>
      <div className="mt-6 space-y-4">
        {connections.map((connection) => (
          <article key={connection.connectionId} className="rounded-xl border border-solitude-500 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-black-pearl-500">{formatPlatform(connection.platform)}</p>
                <p className="mt-1 text-xs text-lynch-500">Account {connection.accountId}</p>
              </div>
              <div className="rounded-full bg-solitude-500 px-3 py-1 text-xs font-semibold text-[#00509D]">
                {connection.lastSyncedDate || 'Pending'}
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-6 rounded-xl bg-solitude-500 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Connected accounts</p>
        <p className="mt-2 text-xl font-semibold text-black-pearl-500">{connections.length}</p>
        <p className="mt-2 text-xs text-lynch-500">
          Each enabled connection is checked by the shared hourly ad-sync worker.
        </p>
      </div>
    </section>
  );
}

function parseDateRangeParam(value) {
  if (VALID_SPECIAL_DATE_RANGES.has(value)) {
    return {
      special: value,
      startDate: null,
      endDate: null,
    };
  }

  if (typeof value === 'string' && value.includes('-')) {
    const [startDate, endDate] = value.split('-').map((part) => Number.parseInt(part.trim(), 10));

    if (Number.isFinite(startDate) && Number.isFinite(endDate) && startDate <= endDate) {
      return {
        special: null,
        startDate,
        endDate,
      };
    }
  }

  return {
    special: DEFAULT_PRESET_DATE,
    startDate: null,
    endDate: null,
  };
}

function serializeDateRange({ special, startDate, endDate }) {
  if (special) {
    return special;
  }

  if (Number.isFinite(startDate) && Number.isFinite(endDate)) {
    return `${startDate}-${endDate}`;
  }

  return DEFAULT_PRESET_DATE;
}
